/* eslint-disable @typescript-eslint/no-explicit-any */
import type { MouseEvent } from "react";
import type { HotspotRecord } from "./mapMarkers";

/**
 * Bounds as [[west, south], [east, north]] format for Mapbox fitBounds
 */
export type GeoJSONBounds = [[number, number], [number, number]];

/**
 * Extract all coordinates from a GeoJSON geometry recursively
 */
const extractCoordinates = (geometry: GeoJSON.Geometry): [number, number][] => {
  switch (geometry.type) {
    case "Point":
      return [[geometry.coordinates[0], geometry.coordinates[1]]];
    case "MultiPoint":
    case "LineString":
      return geometry.coordinates.map((c) => [c[0], c[1]]);
    case "MultiLineString":
    case "Polygon":
      return geometry.coordinates.flat().map((c) => [c[0], c[1]]);
    case "MultiPolygon":
      return geometry.coordinates.flat(2).map((c) => [c[0], c[1]]);
    case "GeometryCollection":
      return geometry.geometries.flatMap(extractCoordinates);
    default:
      return [];
  }
};

/**
 * Calculate bounds from a GeoJSON FeatureCollection
 * Returns bounds in [[west, south], [east, north]] format for Mapbox fitBounds
 * Returns null if no valid coordinates found
 */
export const calculateBoundsFromGeoJSON = (
  geoJSON: GeoJSON.FeatureCollection,
): GeoJSONBounds | null => {
  if (!geoJSON.features || geoJSON.features.length === 0) {
    return null;
  }

  const allCoordinates = geoJSON.features.flatMap((feature) =>
    feature.geometry ? extractCoordinates(feature.geometry) : [],
  );

  if (allCoordinates.length === 0) {
    return null;
  }

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  for (const [lng, lat] of allCoordinates) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

/**
 * Calculate radius in meters from center to edge of viewport
 * Uses Haversine formula to calculate distance
 */
export const calculateViewportRadius = (map: any): number => {
  const bounds = map.getBounds();
  const center = map.getCenter();
  const ne = bounds.getNorthEast();

  // Haversine formula to calculate distance between two points
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (center.lat * Math.PI) / 180;
  const φ2 = (ne.lat * Math.PI) / 180;
  const Δφ = ((ne.lat - center.lat) * Math.PI) / 180;
  const Δλ = ((ne.lng - center.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

/**
 * Calculate distance between two coordinates in meters
 */
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Cluster hotspots that are within a certain radius of each other
 */
export const clusterHotspots = (
  hotspots: HotspotRecord[],
  radiusMeters: number,
  onClick?: (e: MouseEvent<HTMLElement>, lat: number, lng: number) => void,
  label: string = "woningen",
  sessionID?: string,
): HotspotRecord[] => {
  if (hotspots.length === 0) return [];

  // Separate hotspots that should not be clustered
  const noClusterHotspots = hotspots.filter((h) => h.noClustering);
  const clusterableHotspots = hotspots.filter((h) => !h.noClustering);

  const clusters: HotspotRecord[][] = [];
  const processed = new Set<number>();

  clusterableHotspots.forEach((hotspot, index) => {
    if (processed.has(index)) return;

    const cluster: HotspotRecord[] = [hotspot];
    processed.add(index);

    // Find all hotspots within radius
    clusterableHotspots.forEach((other, otherIndex) => {
      if (processed.has(otherIndex)) return;

      const distance = calculateDistance(
        hotspot.lat,
        hotspot.lng,
        other.lat,
        other.lng,
      );

      if (distance <= radiusMeters) {
        cluster.push(other);
        processed.add(otherIndex);
      }
    });

    clusters.push(cluster);
  });

  // Create merged hotspots from clusters
  const clusteredHotspots = clusters.map((cluster) => {
    if (cluster.length === 1) {
      return cluster[0];
    }

    // Calculate center of cluster
    const avgLat = cluster.reduce((sum, h) => sum + h.lat, 0) / cluster.length;
    const avgLng = cluster.reduce((sum, h) => sum + h.lng, 0) / cluster.length;

    // Create stable cluster ID based on sorted property IDs
    const sortedIds = cluster
      .map((h) => h.id)
      .sort()
      .join("-");

    const clusterID = sessionID
      ? `${sessionID}-cluster-${sortedIds}`
      : `cluster-${sortedIds}`;

    return {
      id: clusterID,
      lat: avgLat,
      lng: avgLng,
      type: "cluster" as const,
      title: `${cluster.length} ${label}`,
      onClick: (e: MouseEvent<HTMLElement>) => onClick?.(e, avgLat, avgLng),
    };
  });

  // Return non-clustered hotspots first, then clustered ones
  return [...noClusterHotspots, ...clusteredHotspots];
};
